import {
  RADAR_FRAME,
  RADAR_MAX_UNITS,
  precipClass,
  radarCellAt,
  radarMmH,
} from "@divespots/contracts";
import type { RadarFrame } from "@divespots/contracts";
import { colorAt } from "../precip/raster.ts";
import type { Rgba } from "../precip/raster.ts";

/** Colour of every cell value 0..255: `precipClass(radarMmH(v))`; dry and nodata stay transparent. */
export function radarLut(colors: readonly Rgba[]): (Rgba | null)[] {
  return Array.from({ length: 256 }, (_, v) => {
    const mm = radarMmH(v);
    return mm === null ? null : colorAt(mm, colors);
  });
}

/** One RGBA pixel per frame cell, plus how many are drawn (per class). */
export function frameImage(
  frame: RadarFrame,
  lut: readonly (Rgba | null)[],
  /** A buffer of the right size to fill (cleared first) instead of a new one per frame. */
  into?: Uint8ClampedArray<ArrayBuffer>,
): { data: Uint8ClampedArray<ArrayBuffer>; drawn: number; byClass: number[] } {
  const { cells } = frame;
  const data = into ?? new Uint8ClampedArray(cells.length * 4);
  if (into) data.fill(0);
  const byClass = [0, 0, 0, 0];
  let drawn = 0;
  for (let k = 0; k < cells.length; k++) {
    const v = cells[k]!;
    const c = lut[v];
    if (!c) continue;
    byClass[precipClass(radarMmH(v)!)]!++;
    const o = k * 4;
    data[o] = c[0];
    data[o + 1] = c[1];
    data[o + 2] = c[2];
    data[o + 3] = c[3];
    drawn++;
  }
  return { data, drawn, byClass };
}

export interface RadarReading {
  /** Frame time (end of the 5-minute interval). */
  timeMs: number;
  /** No cell value there (outside the frame or nodata): "keine Radardaten", never dry. */
  nodata: boolean;
  /** Below 0.1 mm/h: no precipitation. */
  dry: boolean;
  /** One decimal, mm/h; with `capped` it is the 25 mm/h ceiling ("≥ 25"). */
  mm: number;
  capped: boolean;
}

/** The measured value at a point; outside the frame or on a nodata cell it is `nodata`. */
export function readRadar(frame: RadarFrame, lat: number, lon: number): RadarReading {
  const none: RadarReading = {
    timeMs: frame.timeMs,
    nodata: true,
    dry: false,
    mm: 0,
    capped: false,
  };
  const cell = radarCellAt(lat, lon);
  if (!cell) return none;
  const v = frame.cells[cell.j * RADAR_FRAME.width + cell.i]!;
  const mm = radarMmH(v);
  if (mm === null) return none;
  return {
    timeMs: frame.timeMs,
    nodata: false,
    dry: precipClass(mm) < 0,
    mm: Math.round(mm * 10) / 10,
    capped: v >= RADAR_MAX_UNITS,
  };
}
